/**
 * The waiting room, in the SPA (spec v1.2 FR-G2/G3) — what /@handle shows a
 * signed-in visitor when the door does not exist yet.
 *
 * The FR-G3.3 expectations render VERBATIM before the field, because managing
 * expectation is what prevents the "I wasted my time" outcome: this is not a
 * knock, they may never open, we will not email them for you.
 *
 * IT ENDS AT THE REGISTRATION (owner, 2026-08-27). This screen used to keep
 * going: a "You're #14", the advocacy assets, and a way to stop waiting, all on
 * a surface nobody could ever get back to — close the pane and the line you
 * wrote was gone. A spot is a CONVERSATION now, so registering hands off to the
 * thread that holds it (`spot-thread.tsx`), and the line, the asking tools and
 * the way out all live in there, where they can be found again. This screen is
 * the room and the form, and nothing after.
 *
 * AND IT NO LONGER READS BEFORE IT WRITES (ruling R-0916-3). There was a
 * `GET /waiting/:handle` first, which answered a count this screen printed —
 * "6 people are waiting to reach this door" — and ToU 2.2 §5A.5 says nobody
 * outside the owner may learn that. The endpoint is gone, so the POST is the
 * only round trip: every answer the read carried (this handle is yours, it is
 * suppressed, nobody holds it, the day's lookups are spent) comes back from it
 * instead, with the line still in the field when it does.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { registerWaiting } from "../../lib/api";
import { clearHeldKnock, loadHeldKnock } from "../../lib/draft";
import { Composer } from "../knock/chat";
import { Button, LinkButton } from "../ui/button";
import { Screen } from "../ui/screen";
import type { Land } from "./land";

/** FR-G3.3, near-verbatim: the three sentences that keep this honest. */
export const NOT_A_KNOCK_COPY =
  "This is not a knock. You are telling us you want to reach this person — nothing is assessed and nothing is charged.";
export const MAY_NEVER_COPY =
  "They have not signed up, and they may never open this door. If they do, they will see you were waiting, and you can knock properly then.";
export const NO_OUTREACH_COPY =
  "We will not email them on your behalf. The fastest way to get this door opened is to ask them yourself.";

export const OWN_HANDLE_COPY =
  "This handle is yours. There is nothing to wait for — open your door.";

/**
 * The register path's refusals, one copy each. The spot thread's "Hold the
 * spot again" goes through the same POST and must say the same things, so it
 * imports these rather than growing its own wording.
 */
export const REGISTER_REFUSED_COPY =
  "That line is not going to work here. Say what you actually want.";
export const REGISTER_LIMIT_COPY = "That's enough waiting rooms for today.";
export const REGISTER_FAILED_COPY = "Nob is not answering. Try that again.";
/** X said nobody holds the handle — a known answer, never dressed as an outage. */
export const NO_SUCH_HANDLE_COPY = "Nobody on X has that handle.";
export const HANDLE_UNRESOLVED_COPY = `${NO_SUCH_HANDLE_COPY} Check the spelling, or search X.`;

/**
 * THE DAY'S LOOKUPS ARE SPENT (ruling R-0910-2 #4).
 *
 * Two sentences because they are addressed to two different people, and the
 * difference is what each of them can do about it: somebody on Free is being
 * offered the thing that lifts the limit, somebody who already pays us is being
 * told the honest thing, which is that tomorrow is when it resets. A single
 * sentence would have to be wrong for one of them, and it would be wrong for the
 * one who has already paid.
 *
 * NEITHER SAYS THE NUMBER, and that is ruling C2 rather than a style choice: the
 * allowance is a server-side constant, published it would be a promise we would
 * have to break the first time X reprices, and `plans/capacity.test.ts` greps
 * the built bundle to keep it out of here.
 */
export const LOOKUP_LIMIT_FREE_COPY =
  "That's your lookup limit for today — upgrade to a paid plan, or search again tomorrow.";
export const LOOKUP_LIMIT_PAID_COPY =
  "That's your lookup limit for today — search again tomorrow, or upgrade your plan to search more.";

/**
 * WHAT REPLACED THE COUNT (ruling R-0916-3). "N people are waiting to reach
 * this door" was the disclosure; this is what is left that is true without
 * being an answer — it says the same thing at every handle, whether six people
 * are waiting or nobody is, so reading it tells the reader nothing about this
 * one. "Nobody is waiting yet. Be the first." is the sentence it cannot become:
 * that one IS a count.
 */
export const MAY_BE_WAITING_COPY =
  "Others may be waiting to reach this door. Nobody is shown how many.";

type Phase =
  | { kind: "ready" }
  | { kind: "own" }
  | { kind: "gone" }
  | { kind: "error"; message: string }
  /**
   * A REFUSAL, WHICH IS NOT AN ERROR (ruling R-0910-2 #4). The distinction is
   * the "Try again" link: an error gets one because retrying is the fix, and a
   * lookup limit must not, because the answer would be the same and offering the
   * press would be the product pretending it might not be.
   */
  | { kind: "refused"; message: string };

type Props = {
  handle: string;
  onSessionExpired: () => void;
  /** The owner path: this is their handle — take them to onboarding. */
  onClaim: () => void;
  /** The spot exists: hand over to the conversation that holds it. */
  onRegistered: Land;
};

export function WaitingArea({ handle, onSessionExpired, onClaim, onRegistered }: Props) {
  const [phase, setPhase] = useState<Phase>({ kind: "ready" });
  /**
   * THE LINE SURVIVES THE TRIP TO X (owner item 10, 2026-08-27).
   *
   * The signed-out poster at this address takes the line before it sends
   * anybody to the provider — writing is the work worth keeping, and asking
   * somebody to prove who they are before they have said anything is the order
   * this product refuses at a live door too (`knock/pre-auth.tsx`). It holds
   * the words under the same key the live door's composer uses, and this is the
   * other end of that: the room somebody lands in opens with what they typed.
   *
   * Only for THIS handle. A held line belongs to the address it was written at.
   */
  const held = useRef(loadHeldKnock());
  const [line, setLine] = useState(held.current?.handle === handle ? held.current.body : "");
  const [busy, setBusy] = useState(false);
  /**
   * A register that was refused, said BESIDE the field rather than instead of
   * it. The refusal used to replace the whole room with an error screen, which
   * threw the field away — and the line in it, which is exactly what somebody
   * who just met "say what you actually want" needs in front of them to fix.
   * Only the ROOM read failing still takes the screen: there is no field yet
   * and nothing typed to protect.
   */
  const [refusal, setRefusal] = useState<string | null>(null);

  const expired = useRef(onSessionExpired);
  expired.current = onSessionExpired;
  const landed = useRef(onRegistered);
  landed.current = onRegistered;

  const submit = useCallback(() => {
    const text = line.trim();
    if (!text || busy) return;
    setBusy(true);
    setRefusal(null);
    void (async () => {
      const result = await registerWaiting(handle, text);
      setBusy(false);
      if (result.ok) {
        // The held copy has done its job. Leaving it would re-seed this field
        // the next time somebody opened any waiting room at this handle, with a
        // line they have already registered.
        clearHeldKnock();
        landed.current(handle, text);
        return;
      }
      switch (result.code) {
        case "UNAUTHENTICATED":
          return expired.current();
        case "OWN_HANDLE":
          return setPhase({ kind: "own" });
        // The spot is held already, which is not a refusal to report: the line
        // that STANDS is the one that came back, and the thread holding it is
        // where this person was going anyway.
        case "ALREADY_REGISTERED":
          return landed.current(handle, result.line ?? "");
        case "HANDLE_SUPPRESSED":
          return setPhase({ kind: "gone" });
        case "HANDLE_UNRESOLVED":
          return setPhase({ kind: "error", message: HANDLE_UNRESOLVED_COPY });
        // The day's lookups are spent. Said as the refusal it is, and never as
        // "Nob is not answering" — Nob answered, and this is what it said.
        case "LOOKUP_LIMIT_FREE":
          return setPhase({ kind: "refused", message: LOOKUP_LIMIT_FREE_COPY });
        case "LOOKUP_LIMIT_PAID":
          return setPhase({ kind: "refused", message: LOOKUP_LIMIT_PAID_COPY });
        case "REGISTRATION_REFUSED":
          return setRefusal(REGISTER_REFUSED_COPY);
        case "REGISTRATION_LIMIT":
          return setRefusal(REGISTER_LIMIT_COPY);
        default:
          return setRefusal(REGISTER_FAILED_COPY);
      }
    })();
  }, [busy, handle, line]);

  /**
   * THE POSTER'S SEND, FINISHED HERE (F-26 entry 3, owner ruling).
   *
   * The signed-out poster took this line and a press of Send before it left
   * for X — the OAuth trip was an interruption, not a withdrawal of intent —
   * so the room it comes back to does not re-ask. One attempt, through the
   * same `submit` a manual send uses: on success everything a manual register
   * does happens (the spot thread, the held copy cleared); on a refusal the
   * line stands in the field with the refusal beside it and the next press is
   * the person's. The ref is what makes it ONE attempt — never a loop — and
   * a spot already held there answers ALREADY_REGISTERED, which lands in the
   * thread holding it exactly as a manual send would.
   *
   * There is no room read to gate it on any more (R-0916-3): the POST itself
   * is what answers "is this handle yours, suppressed, or nobody's", and each
   * of those answers draws its ordinary screen when it arrives.
   */
  const sent = useRef(false);
  useEffect(() => {
    if (sent.current || phase.kind !== "ready") return;
    if (held.current?.handle !== handle || !held.current.body.trim()) return;
    sent.current = true;
    submit();
  }, [handle, phase, submit]);

  if (phase.kind === "gone") {
    return (
      <Screen>
        <p className="text-[15px] text-text-soft" role="alert">
          That door is not available.
        </p>
      </Screen>
    );
  }

  if (phase.kind === "own") {
    return (
      <Screen>
        <p className="text-[17px] text-text">{OWN_HANDLE_COPY}</p>
        <p className="mt-6">
          <Button type="button" onClick={onClaim}>
            Open your door
          </Button>
        </p>
      </Screen>
    );
  }

  if (phase.kind === "refused") {
    return (
      <Screen>
        <p className="text-[15px] text-text-soft" role="alert">
          {phase.message}
        </p>
      </Screen>
    );
  }

  if (phase.kind === "error") {
    return (
      <Screen>
        <p className="text-[15px] text-text-soft" role="alert">
          {phase.message}
        </p>
        <p className="mt-6">
          <LinkButton type="button" onClick={() => setPhase({ kind: "ready" })}>
            Try again
          </LinkButton>
        </p>
      </Screen>
    );
  }

  return (
    <Screen>
      <p className="font-mono text-xs tracking-[0.18em] text-faint">@{handle}</p>
      <h1 className="mt-2 text-2xl font-semibold tracking-tight text-text">No Nob here yet.</h1>
      <p className="mt-3 text-[15px] text-text-soft">{MAY_BE_WAITING_COPY}</p>

      {/* FR-G3.3: the expectations, before the field, in full. */}
      <div className="mt-6 space-y-2 border-l-2 border-border pl-4 text-sm leading-relaxed text-muted">
        <p>{NOT_A_KNOCK_COPY}</p>
        <p>{MAY_NEVER_COPY}</p>
        <p>{NO_OUTREACH_COPY}</p>
      </div>

      <div className="mt-8">
        <Composer onSubmit={submit} disabled={busy || line.trim().length < 3}>
          <label htmlFor="waiting-line" className="sr-only">
            What do you want from them?
          </label>
          <input
            id="waiting-line"
            type="text"
            maxLength={280}
            placeholder="One line: what do you want from them?"
            value={line}
            onChange={(e) => setLine(e.target.value)}
            disabled={busy}
            className="min-w-0 flex-1 rounded-2xl border border-border bg-surface/60 px-4 py-2.5
                       text-[15px] leading-relaxed text-text placeholder:text-fainter
                       focus-visible:border-border-loud focus-visible:outline-none focus-visible:ring-2
                       focus-visible:ring-focus/40"
          />
        </Composer>
        {refusal && (
          <p role="alert" className="mt-2 text-sm leading-relaxed text-muted">
            {refusal}
          </p>
        )}
        <p className="mt-2 text-xs text-fainter">Free. One line, 280 characters.</p>
      </div>
    </Screen>
  );
}
